import { FC, useEffect, useState } from "react";
import { TeamReference } from "@/models/pdfData";
import { Box } from "@mui/material";
import { useAppDataContext } from "@context/AppContext";
import { Positions } from "../TeamLineup";
import { Player } from "@/models/gameGeneral";
import { gameState } from "@/services/gameState";
import { Translate } from "@mui/icons-material";
import { ConnectionStatus } from "@/models/ticker";

type ColorVariants = "default" | "setter" | "libero" | "unset";

type CaptainVariants = "none" | "gameCaptain" | "teamCaptain";

type DecorationVariants = "default" | "backupCaptain" | "backupSetterCaptain";

const backgroundColors: { [key in ColorVariants]: string } = {
  default: "#e9effa",
  setter: "#1a365d",
  libero: "#feda56",
  unset: "#e9effa",
};

const textColors: { [key in ColorVariants]: string } = {
  default: "#000",
  setter: "#fff",
  libero: "#000",
  unset: "#00000010",
};

const textDecorationColors: { [key in DecorationVariants]: string } = {
  default: "#000",
  backupCaptain: "#00000033",
  backupSetterCaptain: "#FFFFFFA5",
};

const grayscaleValues: { [key in ConnectionStatus]: number } = {
  connected: 0,
  loosing: 0.6,
  lost: 1,
  paused: 0.4,
};

const PlayerBox: FC<{
  position: Positions;
  player?: Player;
  team: TeamReference;
  teamCaptainOnField: boolean;
}> = ({ position, player, team, teamCaptainOnField }) => {
  const teamSquads = gameState("teamSquads");
  const connectionStatus = gameState("connectionStatus");
  const liberoExchangePlayer = gameState("liberoExchangePlayer");
  const substitutions = gameState("substitutions");
  const currentScore = gameState("currentScore");
  const isSetterPlayerKnown = gameState("isSetterPlayerKnown");

  const {
    highlightedSetter,
    highlightedGameCaptain,
    highlightSetterAutomatically,
    useHighlightFor,
    showPlayerNames,
    usePlayerFirstNames,
    highlightPlayer,
    currentView,
  } = useAppDataContext();
  const [playerIsLibero, setPlayerIsLibero] = useState(false);
  const [playerIsSetter, setPlayerIsSetter] = useState(false);
  const [playerIsTeamCaptain, setPlayerIsTeamCaptain] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    backgroundColors.default,
  );
  const [textColor, setTextColor] = useState(textColors.default);
  const [captainVariant, setCaptainVariant] = useState<CaptainVariants>("none");
  const [substitutedPlayer, setSubstitutedPlayer] = useState<Player>();

  const onClick = () => {
    if (player) {
      const highlightFor =
        highlightSetterAutomatically && isSetterPlayerKnown
          ? "gameCaptain"
          : useHighlightFor;
      highlightPlayer(team, player.uuid, highlightFor);
    }
  };

  useEffect(() => {
    setPlayerIsLibero(
      teamSquads?.[team]?.liberos?.some(({ uuid }) => uuid === player?.uuid) ||
        false,
    );
    setPlayerIsTeamCaptain(
      !!player?.uuid && teamSquads?.[team]?.captain?.uuid === player?.uuid,
    );
    setPlayerIsSetter(
      (isSetterPlayerKnown &&
        highlightSetterAutomatically &&
        player?.position === "SETTER") ||
        ((!highlightSetterAutomatically || !isSetterPlayerKnown) &&
          highlightedSetter[team] === player?.uuid),
    );

    const currentSubstitutionsForPlayer = substitutions[team].find(
      (s) => s.score === currentScore && s.playerInId === player?.uuid,
    );

    if (currentSubstitutionsForPlayer) {
      setSubstitutedPlayer(
        teamSquads[team].players.find(
          (p) => p.uuid === currentSubstitutionsForPlayer.playerOutId,
        ),
      );
    } else {
      setSubstitutedPlayer(undefined);
    }
  }, [
    position,
    teamSquads,
    player,
    highlightedSetter,
    highlightedGameCaptain,
    highlightSetterAutomatically,
    isSetterPlayerKnown,
    team,
    substitutions,
    currentScore,
  ]);

  useEffect(() => {
    let colorVariant: ColorVariants;
    if (!player) {
      colorVariant = "unset";
    } else if (playerIsSetter) {
      colorVariant = "setter";
    } else if (playerIsLibero) {
      colorVariant = "libero";
    } else {
      colorVariant = "default";
    }
    setTextColor(textColors[colorVariant]);
    setBackgroundColor(backgroundColors[colorVariant]);

    if (!player) {
      setCaptainVariant("none");
    } else if (playerIsTeamCaptain) {
      setCaptainVariant("teamCaptain");
    } else if (highlightedGameCaptain[team] === player.uuid) {
      setCaptainVariant("gameCaptain");
    } else {
      setCaptainVariant("none");
    }
  }, [
    player,
    playerIsTeamCaptain,
    playerIsLibero,
    playerIsSetter,
    highlightSetterAutomatically,
    highlightedSetter,
    highlightedGameCaptain,
    teamCaptainOnField,
    useHighlightFor,
  ]);

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      sx={{
        aspectRatio: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gridArea: position,
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        height: "auto",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          aspectRatio: 1,
          width: "100%",
          height: "auto",
          borderRadius: "30%",
          display: "grid",
          gridTemplateRows: "1fr auto 1fr",
          gridTemplateColumns: "1fr",
          justifyItems: "center",
          filter: `grayscale(${grayscaleValues[connectionStatus]})`,
          background: backgroundColor,
          transition: "filter 1s",
        }}
      >
        <Box
          sx={{
            gridRow: 1,
            color: textColor,
            fontSize: "0.35em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "center",
            pb: "0.1em",
            alignSelf: "baseline",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.2em",
            fontFamily: "Lato",
            opacity: playerIsLibero ? 0.5 : 1,
          }}
        >
          <Box
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentView === "tablet" && player && playerIsLibero
              ? `( ${liberoExchangePlayer[team]?.jerseyNumber} )`
              : ""}
            {currentView === "tablet" && player && substitutedPlayer
              ? `( ${substitutedPlayer.jerseyNumber} )`
              : ""}
          </Box>
        </Box>
        <Box
          sx={{
            minWidth: "100%",
            gridRow: 2,
            color: textColor,
            transition: "filter 1s",
            fontSize: "1.3em",
            textDecoration: captainVariant !== "none" ? "underline" : "",
            textDecorationStyle:
              captainVariant === "gameCaptain" ? "dotted" : "solid",
            textDecorationColor:
              playerIsTeamCaptain || !teamCaptainOnField
                ? textColor
                : playerIsSetter
                  ? "#ffffff88"
                  : "#00000033",
            textDecorationThickness: playerIsTeamCaptain ? "0.11em" : "0.08em",
            textUnderlineOffset: "0.05em",
            fontFamily: "Lato",
            fontWeight: 700,
            letterSpacing: "0.07em",
            textIndent: "0.07em",
            textAlign: "center",
          }}
        >
          {player ? player.jerseyNumber : position}
        </Box>
        <Box
          sx={{
            gridRow: 3,
            color: textColor,
            fontSize: "0.25em",
            maxWidth: "70%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "70%",
            textAlign: "center",
            pb: "0.1em",
            alignSelf: "end",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.2em",
          }}
        >
          {player && showPlayerNames && !player.isNationalityGerman && (
            <Translate sx={{ fontSize: "0.6em" }} />
          )}
          <Box
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {player && showPlayerNames && currentView === "tablet"
              ? `${player[usePlayerFirstNames ? "firstName" : "lastName"]}`
              : ""}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlayerBox;
